import { useSceneStore } from '@/stores/scene-store';
import { encodeFieldKey, EntityPath } from '@/schemas/validation/ValidationSchemaGenerator';
import { SceneDataElement } from '@/schemas/sceneDataSchema';

type StoreFieldValue = SceneDataElement['value'];

/**
 * Subscribe to live store field values and interpolate them into a text template.
 * Template uses positional `{}` placeholders that map to the store_fields array.
 *
 * Example: text="Hello, {} {}" with store_fields pointing to first_name and last_name
 * produces "Hello, John Smith" and updates reactively as the user types.
 *
 * Returns the original text unchanged when no fields are provided.
 */
export function useStoreFields(text: string, fields?: EntityPath[]): string {
  return useSceneStore((state) => {
    if (!fields?.length) return text;

    const values = fields.map((field) => {
      const key = encodeFieldKey(field);
      return state.pendingChanges[key] ?? state.serverState[key]?.value ?? null;
    });

    return interpolate(text, values);
  });
}

function interpolate(text: string, values: StoreFieldValue[]): string {
  const segments = text.split('{}');
  return segments.map((segment, i) => (i < segments.length - 1 ? segment + String(values[i] ?? '') : segment)).join('');
}
